import { useMemo, useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { DeepLink } from "../components/DeepLink";
import { resolveCitationLink } from "../lib/deeplinks";

const MODES = [
  { id: "kpis",      label: "KPIs",       desc: "Welche Kennzahlen?" },
  { id: "use_cases", label: "Use Cases",  desc: "KI-Anwendungsfälle" },
  { id: "roi",       label: "ROI",        desc: "Wirtschaftlichkeit"  },
  { id: "strategy",  label: "Strategie",  desc: "Sparring & Stress-Test" },
] as const;

const PERSONAS = [
  { id: "CEO",     label: "CEO — Geschäftsführer" },
  { id: "CFO",     label: "CFO — Finanzen" },
  { id: "CMO",     label: "CMO — Marketing" },
  { id: "COO",     label: "COO — Operations" },
  { id: "CTO",     label: "CTO — Technologie" },
  { id: "CRO",     label: "CRO — Vertrieb" },
  { id: "CSO",     label: "CSO — Strategie" },
  { id: "CIO",     label: "CIO — IT" },
  { id: "CHRO",    label: "CHRO — HR" },
  { id: "CDO",     label: "CDO — Daten" },
  { id: "Berater", label: "Externer Berater" },
  { id: "Branche", label: "Branchen-Benchmark" },
] as const;

type Mode = (typeof MODES)[number]["id"];
type Persona = (typeof PERSONAS)[number]["id"];

/** Beleg-Chip aus der Hybrid-Wissensbasis (BEN-91). */
type Citation = {
  n?: number;
  label?: string;
  source?: string | null;
  snippet?: string | null;
  path?: string | null;
  href?: string | null;
};

type ChatMsg = {
  role: "user" | "assistant";
  content: string;
  runId?: string;
  citations?: Citation[];
  grounded?: boolean;
  degraded?: boolean;
  engine?: string | null;
};

type ChatResponse = {
  response: string;
  suggestions: string[];
  mode: Mode;
  persona: Persona;
  run_id: string;
  trace_url: string;
  // BEN-91: Advisor grounded aus derselben Hybrid-Wissensbasis wie der Schritt.
  grounded?: boolean;
  degraded?: boolean;
  retrieval_engine?: string | null;
  citations?: Citation[];
};

export default function AdvisorPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [mode, setMode] = useState<Mode>("kpis");
  const [persona, setPersona] = useState<Persona>("CEO");
  const [history, setHistory] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const initialFromURL = searchParams.get("msg");
  const initialMode = (searchParams.get("mode") as Mode) || undefined;
  const initialPersona = (searchParams.get("persona") as Persona) || undefined;
  useEffect(() => {
    if (initialMode && initialMode !== mode) setMode(initialMode);
    if (initialPersona && initialPersona !== persona) setPersona(initialPersona);
    if (initialFromURL && !sending) {
      setInput(initialFromURL);
      setTimeout(() => { void send(initialFromURL); }, 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load suggestions on mount or when mode/persona changes (call with empty msg)
  useEffect(() => {
    setHistory([]);
    setSuggestions([]);
    void send("", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, persona]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [history, suggestions]);

  async function send(text: string, openerOnly = false) {
    if (sending) return;
    const message = text.trim();
    if (!openerOnly && !message) return;
    setSending(true);
    const newHist: ChatMsg[] = openerOnly ? [] : [...history, { role: "user", content: message }];
    if (!openerOnly) setHistory(newHist);
    try {
      const r = await api.post<ChatResponse>("/advisor/chat", {
        message: openerOnly ? "" : message,
        mode,
        persona,
        history: newHist.slice(-10),
      });
      const asMsg: ChatMsg = {
        role: "assistant",
        content: r.response,
        runId: r.run_id || undefined,
        citations: r.citations ?? [],
        grounded: r.grounded,
        degraded: r.degraded,
        engine: r.retrieval_engine,
      };
      setHistory(openerOnly ? [asMsg] : [...newHist, asMsg]);
      setSuggestions(r.suggestions || []);
      setInput("");
    } catch (e) {
      const err = e instanceof Error ? e.message : String(e);
      setHistory([...newHist, { role: "assistant", content: `⚠️ Fehler: ${err}` }]);
    } finally {
      setSending(false);
    }
  }

  const tenantLabel = useMemo(
    () => (effectiveTenantId === "tnt_demo" ? "Demo-Sandbox" : effectiveTenantId),
    [effectiveTenantId]
  );

  return (
    <section className="advisor-page">
      <header className="advisor-hero">
        <h1>KI-Berater</h1>
        <p className="muted">
          Persona-spezifisches Sparring in 4 Modi. Sie sind in Tenant <code>{tenantLabel}</code>.
          {effectiveTenantId === "tnt_demo" && (
            <span className="data-quality-badge"> · Demo-Antworten · keine echten Tenant-Daten</span>
          )}
        </p>
      </header>

      <div className="advisor-controls">
        <div className="advisor-modes">
          {MODES.map((m) => (
            <button
              key={m.id}
              className={`mode-pill ${mode === m.id ? "active" : ""}`}
              onClick={() => setMode(m.id)}
              title={m.desc}
            >
              <strong>{m.label}</strong>
              <span className="muted small">{m.desc}</span>
            </button>
          ))}
        </div>
        <div className="advisor-persona">
          <label htmlFor="persona">Persona / Rolle</label>
          <select id="persona" value={persona} onChange={(e) => setPersona(e.target.value as Persona)}>
            {PERSONAS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="advisor-chat" ref={scrollRef}>
        {history.length === 0 && (
          <div className="advisor-empty muted small">Stelle eine Frage oder wähle einen Vorschlag.</div>
        )}
        {history.map((m, i) => (
          <div key={i} className={`chat-bubble ${m.role}`}>
            <div className="chat-meta">
              {m.role === "user" ? "Sie" : "KI-Berater"}
            </div>
            <div className="chat-content">{renderMarkdown(m.content)}</div>
            {m.role === "assistant" && (m.grounded || m.degraded || (m.citations?.length ?? 0) > 0 || m.runId) && (
              <div className="advisor-grounding">
                {(m.grounded || (m.citations?.length ?? 0) > 0) && !m.degraded && (
                  <span className="advisor-grounded-badge" title={m.engine ? `Retrieval: ${m.engine}` : "Hybrid-Wissensbasis (BM25 + Vektor)"}>
                    ✓ Grounded aus der 1&amp;1-Wissensbasis
                    {(m.citations?.length ?? 0) > 0 ? ` · ${m.citations!.length} Belege` : ""}
                  </span>
                )}
                {m.degraded && (
                  <span className="advisor-degraded-badge" title="Das lokale Modell war nicht erreichbar.">
                    ⚠️ Modell nicht erreichbar — Belege stammen dennoch aus den echten Daten
                  </span>
                )}
                {(m.citations?.length ?? 0) > 0 && (
                  <div className="advisor-citations" aria-label="Belege aus der Wissensbasis">
                    {m.citations!.map((c, ci) => {
                      const link = resolveCitationLink(c);
                      return (
                        <span key={ci} className="advisor-cite" title={c.snippet ?? undefined}>
                          <span className="advisor-cite-n">[{c.n ?? ci + 1}]</span>
                          <span className="advisor-cite-label">{c.label ?? c.source ?? "Beleg"}</span>
                          {link && (
                            <DeepLink href={link.href} label={link.label} kind={link.kind} className="advisor-cite-link" />
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
                {m.runId && (
                  <Link className="advisor-run-link" to={`/app/agents?run=${encodeURIComponent(m.runId)}`} title="Agent-Run mit Evidence öffnen">
                    Beleg-Run ansehen →
                  </Link>
                )}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="chat-bubble assistant" aria-live="polite">
            <div className="chat-meta">KI-Berater</div>
            <div className="chat-typing" aria-label="Antwort wird erstellt">
              <i /><i /><i />
            </div>
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="advisor-suggestions">
          <span className="muted small">Vorschläge:</span>
          {suggestions.map((s, i) => (
            <button key={i} className="suggestion-chip" onClick={() => send(s)} disabled={sending}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="advisor-input"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Frage an KI-Berater (${MODES.find((m) => m.id === mode)?.label} · ${persona}) …`}
          rows={2}
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
        />
        <button type="submit" className="btn primary" disabled={sending || !input.trim()}>
          {sending ? "…" : "Senden"}
        </button>
      </form>
    </section>
  );
}

/** Tiny markdown-ish renderer: paragraphs, lists, bold. No HTML injection. */
function renderMarkdown(text: string): JSX.Element {
  const lines = text.split(/\n/);
  const out: JSX.Element[] = [];
  let listBuf: string[] = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`}>
          {listBuf.map((li, i) => (
            <li key={i}>{inline(li.replace(/^[-*]\s*/, ""))}</li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  for (const ln of lines) {
    if (/^[-*]\s+/.test(ln)) {
      listBuf.push(ln);
    } else {
      flushList();
      if (ln.trim()) out.push(<p key={out.length}>{inline(ln)}</p>);
    }
  }
  flushList();
  return <>{out}</>;
}

function inline(s: string): JSX.Element[] {
  const parts: JSX.Element[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(<span key={i++}>{s.slice(last, m.index)}</span>);
    parts.push(<strong key={i++}>{m[1]}</strong>);
    last = re.lastIndex;
  }
  if (last < s.length) parts.push(<span key={i++}>{s.slice(last)}</span>);
  return parts;
}
